import React, { useEffect, useRef, useState } from 'react';
import './Auth.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

function Particles() {
  const particles = Array.from({ length: 14 }, (_, i) => ({
    id: i,
    left: `${(i * 7 + 4) % 100}%`,
    size: `${(i % 4) + 2}px`,
    duration: `${(i % 10) + 9}s`,
    delay: `${(i % 8)}s`,
  }));
  return (
    <div className="auth-particles" aria-hidden>
      {particles.map((p) => (
        <span key={p.id} className="auth-particle"
          style={{ left: p.left, width: p.size, height: p.size, animationDuration: p.duration, animationDelay: p.delay }}
        />
      ))}
    </div>
  );
}

function IconSun() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>;
}
function IconMoon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></svg>;
}
function IconBack() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><path d="M19 12H5M12 5l-7 7 7 7" /></svg>;
}
function IconEye() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
}
function IconEyeOff() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>;
}
function IconArrow() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><path d="M5 12h14M12 5l7 7-7 7" /></svg>;
}

/**
 * LoginForm
 * The robot is now rendered once at the App root level (persistent).
 * Props:
 *   onBack         : () => void         — go back to AuthPage
 *   onSuccess      : (user, token) => void
 *   isDarkMode     : boolean
 *   onToggleTheme  : () => void
 */
export function LoginForm({ onBack, onSuccess, isDarkMode, onToggleTheme }) {
  const [form, setForm]         = useState({ email: '', password: '' });
  const [errors, setErrors]     = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPw, setShowPw]     = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (apiError) setApiError('');
  }

  function validate() {
    const e = {};
    if (!form.email.trim())    e.email    = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email.';
    if (!form.password)        e.password = 'Password is required.';
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setApiError('');
    try {
      const res  = await fetch(`${API_BASE}/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: form.email.trim(), password: form.password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Login failed. Please try again.');
      onSuccess(data.user, data.token);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-root">
      <Particles />
      <button type="button" className="auth-theme-btn" onClick={onToggleTheme}
        aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
        {isDarkMode ? <IconSun /> : <IconMoon />}
      </button>

      {/* ── Left ── */}
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand-badge"><span className="auth-brand-dot" />AI Powered · Career Guidance</div>
          <h1 className="auth-brand-title">Welcome<br />Back</h1>
          <p className="auth-brand-sub">Log in to continue your personalized career counseling session.</p>
        </div>

        <div className="auth-form-container">
          <button type="button" className="auth-back" onClick={onBack}>
            <IconBack /> Back
          </button>

          {apiError && <div className="auth-api-error" role="alert">⚠ {apiError}</div>}

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                ref={emailRef}
                type="email"
                className={`auth-input${errors.email ? ' has-error' : ''}`}
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                autoComplete="email"
                required
              />
              {errors.email && <span className="auth-error" role="alert">{errors.email}</span>}
            </div>

            {/* Password */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="login-password">Password</label>
              <div className="auth-pw-wrap">
                <input
                  id="login-password"
                  type={showPw ? 'text' : 'password'}
                  className={`auth-input${errors.password ? ' has-error' : ''}`}
                  placeholder="Your password"
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button type="button" className="auth-pw-toggle" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? <IconEyeOff /> : <IconEye />}
                </button>
              </div>
              {errors.password && <span className="auth-error" role="alert">{errors.password}</span>}
            </div>

            <div className="auth-submit-wrap">
              <button id="login-submit-btn" type="submit" className="auth-submit" disabled={loading}>
                {loading ? (<><span className="auth-spinner" /> Logging in…</>) : (<>Login <IconArrow /></>)}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── Right — Greeting overlay (robot is behind, rendered by App root) ── */}
      <div className="auth-right">
        <div className="auth-greeting" role="complementary">
          <p className="auth-greeting-title">Good to see you again! 👋</p>
          <p className="auth-greeting-sub">Log in to pick up where you left off. Your chat history and profile are waiting for you.</p>
        </div>
      </div>
    </div>
  );
}
